import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Chip,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add, Edit, Refresh } from "@material-ui/icons";
import { useHistory } from "react-router-dom";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import WhatsAppModal from "../../components/WhatsAppModal";

const dayLabels = [
  { key: "monday", label: "Segunda" },
  { key: "tuesday", label: "Terça" },
  { key: "wednesday", label: "Quarta" },
  { key: "thursday", label: "Quinta" },
  { key: "friday", label: "Sexta" },
  { key: "saturday", label: "Sábado" },
  { key: "sunday", label: "Domingo" },
];

const defaultBusinessHours = [
  { day: "monday", enabled: true, start: "08:00", end: "18:00" },
  { day: "tuesday", enabled: true, start: "08:00", end: "18:00" },
  { day: "wednesday", enabled: true, start: "08:00", end: "18:00" },
  { day: "thursday", enabled: true, start: "08:00", end: "18:00" },
  { day: "friday", enabled: true, start: "08:00", end: "18:00" },
  { day: "saturday", enabled: false, start: "08:00", end: "12:00" },
  { day: "sunday", enabled: false, start: "08:00", end: "12:00" },
];

const useStyles = makeStyles((theme) => ({
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
    ...theme.scrollbarStyles,
  },
  sectionPaper: {
    padding: theme.spacing(2.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
  },
  sectionDescription: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  settingsGrid: {
    alignItems: "flex-start",
  },
  hoursTable: {
    "& td, & th": {
      whiteSpace: "nowrap",
    },
  },
  connectionCard: {
    padding: theme.spacing(2),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.01) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  connectionTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  connectionName: {
    fontSize: 17,
    fontWeight: 700,
  },
  connectionMeta: {
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  toggleRow: {
    display: "flex",
    gap: theme.spacing(3),
    flexWrap: "wrap",
  },
  connectionActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  queuesHint: {
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  licenseStatusCard: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  licenseStatusMeta: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  licenseReason: {
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
}));

const parseBusinessHours = (rawValue) => {
  if (!rawValue) {
    return defaultBusinessHours;
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return defaultBusinessHours;
    }

    return dayLabels.map((day) => {
      const found = parsed.find((item) => item.day === day.key);
      return found || defaultBusinessHours.find((item) => item.day === day.key);
    });
  } catch (error) {
    return defaultBusinessHours;
  }
};

const createConnectionDraft = (whatsapp) => ({
  name: whatsapp.name,
  greetingMessage: whatsapp.greetingMessage || "",
  farewellMessage: whatsapp.farewellMessage || "",
  allowGroupMessages: Boolean(whatsapp.allowGroupMessages),
  isDefault: Boolean(whatsapp.isDefault),
  defaultQueueId: whatsapp.defaultQueueId || "",
  queueIds: whatsapp.queues?.map((queue) => queue.id) || [],
});

const CompanyPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState([]);
  const [whatsApps, setWhatsApps] = useState([]);
  const [connectionDrafts, setConnectionDrafts] = useState({});
  const [awayMinutesDraft, setAwayMinutesDraft] = useState("5");
  const [ticketSubjectRequiredDraft, setTicketSubjectRequiredDraft] = useState(false);
  const [autoResumeSlaDraft, setAutoResumeSlaDraft] = useState(false);
  const [userCanSeeQueueTicketsDraft, setUserCanSeeQueueTicketsDraft] = useState(true);
  const [businessHours, setBusinessHours] = useState(defaultBusinessHours);
  const [outOfHoursMessageDraft, setOutOfHoursMessageDraft] = useState("");
  const [outOfHoursActionDraft, setOutOfHoursActionDraft] = useState("keep_pending");
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [selectedWhatsAppId, setSelectedWhatsAppId] = useState(null);
  const [licenseStatus, setLicenseStatus] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsResponse, whatsappsResponse, licenseStatusResponse] = await Promise.all([
        api.get("/settings"),
        api.get("/whatsapp"),
        api.get("/licenses/status"),
      ]);

      const settingsData = Array.isArray(settingsResponse.data)
        ? settingsResponse.data
        : [];
      const whatsappsData = Array.isArray(whatsappsResponse.data)
        ? whatsappsResponse.data
        : [];
      const nextLicenseStatus =
        licenseStatusResponse && typeof licenseStatusResponse.data === "object"
          ? licenseStatusResponse.data
          : null;

      setSettings(settingsData);
      setWhatsApps(whatsappsData);
      setAwayMinutesDraft(
        settingsData.find((item) => item.key === "userAwayMinutes")?.value || "5"
      );
      setTicketSubjectRequiredDraft(
        (settingsData.find((item) => item.key === "ticketSubjectRequired")?.value ||
          "disabled") === "enabled"
      );
      setAutoResumeSlaDraft(
        (settingsData.find((item) => item.key === "autoResumeSlaOnCustomerReply")?.value ||
          "disabled") === "enabled"
      );
      setUserCanSeeQueueTicketsDraft(
        (settingsData.find((item) => item.key === "userCanSeeQueueTickets")?.value ||
          "enabled") === "enabled"
      );
      setBusinessHours(
        parseBusinessHours(
          settingsData.find((item) => item.key === "companyBusinessHours")?.value
        )
      );
      setOutOfHoursMessageDraft(
        settingsData.find((item) => item.key === "companyOutOfHoursMessage")?.value || ""
      );
      setOutOfHoursActionDraft(
        settingsData.find((item) => item.key === "companyOutOfHoursAction")?.value ||
          "keep_pending"
      );
      setConnectionDrafts(
        whatsappsData.reduce((acc, whatsapp) => {
          acc[whatsapp.id] = createConnectionDraft(whatsapp);
          return acc;
        }, {})
      );
      setLicenseStatus(nextLicenseStatus);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveSetting = async (key, value) => {
    const { data } = await api.put(`/settings/${key}`, { value });
    setSettings((prevSettings) => {
      const nextSettings = [...prevSettings];
      const index = nextSettings.findIndex((item) => item.key === key);

      if (index >= 0) {
        nextSettings[index] = data;
        return nextSettings;
      }

      return [...nextSettings, data];
    });
  };

  const handleBusinessHourChange = (dayKey, field, value) => {
    setBusinessHours((current) =>
      current.map((item) =>
        item.day === dayKey ? { ...item, [field]: value } : item
      )
    );
  };

  const handleConnectionDraftChange = (whatsappId, field, value) => {
    setConnectionDrafts((current) => ({
      ...current,
      [whatsappId]: {
        ...current[whatsappId],
        [field]: value,
      },
    }));
  };

  const handleSaveConnection = async (whatsappId) => {
    const whatsapp = whatsApps.find((item) => item.id === whatsappId);
    const draft = connectionDrafts[whatsappId];

    if (!whatsapp || !draft) {
      return;
    }

    try {
      await api.put(`/whatsapp/${whatsappId}`, {
        name: draft.name,
        greetingMessage: draft.greetingMessage,
        farewellMessage: draft.farewellMessage,
        allowGroupMessages: draft.allowGroupMessages,
        isDefault: draft.isDefault,
        defaultQueueId: draft.defaultQueueId || null,
        queueIds: draft.queueIds,
      });

      await loadData();
    } catch (error) {
      toastError(error);
    }
  };

  const handleOpenCreateConnection = () => {
    setSelectedWhatsAppId(null);
    setWhatsAppModalOpen(true);
  };

  const handleOpenEditConnection = (whatsappId) => {
    setSelectedWhatsAppId(whatsappId);
    setWhatsAppModalOpen(true);
  };

  const handleSaveCompanyOperation = async () => {
    try {
      await Promise.all([
        handleSaveSetting(
          "userAwayMinutes",
          String(Math.max(1, Number(awayMinutesDraft) || 5))
        ),
        handleSaveSetting(
          "ticketSubjectRequired",
          ticketSubjectRequiredDraft ? "enabled" : "disabled"
        ),
        handleSaveSetting(
          "autoResumeSlaOnCustomerReply",
          autoResumeSlaDraft ? "enabled" : "disabled"
        ),
        handleSaveSetting(
          "userCanSeeQueueTickets",
          userCanSeeQueueTicketsDraft ? "enabled" : "disabled"
        )
      ]);
      await loadData();
    } catch (error) {
      toastError(error);
    }
  };

  const handleSaveBusinessHours = async () => {
    try {
      await Promise.all([
        handleSaveSetting("companyBusinessHours", JSON.stringify(businessHours)),
        handleSaveSetting("companyOutOfHoursMessage", outOfHoursMessageDraft),
        handleSaveSetting("companyOutOfHoursAction", outOfHoursActionDraft),
      ]);
      await loadData();
    } catch (error) {
      toastError(error);
    }
  };

  const businessHoursSummary = useMemo(() => {
    const activeDays = businessHours.filter((item) => item.enabled).length;
    return activeDays ? `${activeDays} dias ativos` : "Nenhum dia configurado";
  }, [businessHours]);

  return (
    <MainContainer>
      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={() => {
          setWhatsAppModalOpen(false);
          setSelectedWhatsAppId(null);
          loadData();
        }}
        whatsAppId={selectedWhatsAppId}
      />

      <MainHeader>
        <Title>Empresa</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Refresh />}
            onClick={loadData}
            disabled={loading}
          >
            Atualizar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {licenseStatus ? (
        <Paper variant="outlined" className={classes.licenseStatusCard}>
          <div className={classes.licenseStatusMeta}>
            <Typography variant="subtitle1">
              {licenseStatus.mode === "central"
                ? "Licença da instalação"
                : "Status da licença"}
            </Typography>
            <Typography variant="body2" className={classes.licenseReason}>
              {licenseStatus.reason
                ? licenseStatus.reason
                : `Validade: ${
                    licenseStatus.expiresAt
                      ? new Date(licenseStatus.expiresAt).toLocaleString("pt-BR")
                      : "não definida"
                  }`}
            </Typography>
          </div>
          <Chip
            label={licenseStatus.valid ? "Licença válida" : "Licença inválida"}
            color={licenseStatus.valid ? "primary" : "secondary"}
          />
        </Paper>
      ) : null}

      <div className={classes.scrollArea}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper className={classes.sectionPaper} variant="outlined">
              <div className={classes.sectionHeader}>
                <div>
                  <Typography className={classes.sectionTitle}>
                    Operação da equipe
                  </Typography>
                  <Typography className={classes.sectionDescription}>
                    Centralize o comportamento operacional da empresa em um único lugar.
                  </Typography>
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveCompanyOperation}
                >
                  Salvar operação
                </Button>
              </div>

              <Grid container spacing={2} className={classes.settingsGrid}>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="number"
                    label="Tempo para considerar ausente (minutos)"
                    value={awayMinutesDraft}
                    inputProps={{ min: 1 }}
                    helperText="Depois desse tempo sem atividade, o atendente passa para ausente."
                    onChange={(event) => setAwayMinutesDraft(event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={7}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={ticketSubjectRequiredDraft}
                        onChange={(event) =>
                          setTicketSubjectRequiredDraft(event.target.checked)
                        }
                      />
                    }
                    label="Assunto do chamado obrigatório"
                  />
                  <Typography variant="body2" color="textSecondary">
                    Quando ativado, o atendente precisa informar o assunto antes de enviar mensagens no chamado.
                  </Typography>
                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={autoResumeSlaDraft}
                          onChange={(event) =>
                            setAutoResumeSlaDraft(event.target.checked)
                          }
                        />
                      }
                      label="Retomar SLA automaticamente"
                    />
                    <Typography variant="body2" color="textSecondary">
                      Quando ativado, o contador Tempo SLA retoma automaticamente quando o cliente responder.
                    </Typography>
                  </Box>
                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={userCanSeeQueueTicketsDraft}
                          onChange={(event) =>
                            setUserCanSeeQueueTicketsDraft(event.target.checked)
                          }
                        />
                      }
                      label="Permitir fila compartilhada para atendentes"
                    />
                    <Typography variant="body2" color="textSecondary">
                      Quando ativado, usuários do perfil atendente podem ver chamados da própria fila (abertos e pendentes), mesmo sem estarem atribuídos a eles.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper className={classes.sectionPaper} variant="outlined">
              <div className={classes.sectionHeader}>
                <div>
                  <Typography className={classes.sectionTitle}>
                    Horário de atendimento
                  </Typography>
                  <Typography className={classes.sectionDescription}>
                    Defina o expediente operacional da empresa, a mensagem de ausência
                    e o comportamento desejado fora do horário ou em feriados.
                  </Typography>
                </div>
                <Box display="flex" gridGap={12} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => history.push("/company-holidays")}
                  >
                    Cadastrar feriados
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveBusinessHours}
                  >
                    Salvar horário
                  </Button>
                </Box>
              </div>

              <Typography variant="body2" color="textSecondary">
                {businessHoursSummary}
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                label="Mensagem de ausência fora do horário"
                value={outOfHoursMessageDraft}
                onChange={(event) => setOutOfHoursMessageDraft(event.target.value)}
                helperText="Essa mensagem é enviada automaticamente em atendimentos individuais e em grupos fora do expediente. Em feriados, a mensagem cadastrada no feriado tem prioridade."
              />

              <FormControl fullWidth variant="outlined">
                <InputLabel>Quando chamarem fora do horário ou em feriado</InputLabel>
                <Select
                  value={outOfHoursActionDraft}
                  onChange={(event) => setOutOfHoursActionDraft(event.target.value)}
                  label="Quando chamarem fora do horário ou em feriado"
                >
                  <MenuItem value="keep_pending">Manter na fila</MenuItem>
                  <MenuItem value="close_ticket">Fechar o chamado</MenuItem>
                </Select>
              </FormControl>

              <Table size="small" className={classes.hoursTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>Dia</TableCell>
                    <TableCell>Ativo</TableCell>
                    <TableCell>Início</TableCell>
                    <TableCell>Fim</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dayLabels.map((day) => {
                    const currentDay =
                      businessHours.find((item) => item.day === day.key) ||
                      defaultBusinessHours.find((item) => item.day === day.key);

                    return (
                      <TableRow key={day.key}>
                        <TableCell>{day.label}</TableCell>
                        <TableCell>
                          <Switch
                            color="primary"
                            checked={Boolean(currentDay?.enabled)}
                            onChange={(event) =>
                              handleBusinessHourChange(
                                day.key,
                                "enabled",
                                event.target.checked
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            variant="outlined"
                            size="small"
                            value={currentDay?.start || "08:00"}
                            onChange={(event) =>
                              handleBusinessHourChange(
                                day.key,
                                "start",
                                event.target.value
                              )
                            }
                            inputProps={{ step: 300 }}
                            disabled={!currentDay?.enabled}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            variant="outlined"
                            size="small"
                            value={currentDay?.end || "18:00"}
                            onChange={(event) =>
                              handleBusinessHourChange(
                                day.key,
                                "end",
                                event.target.value
                              )
                            }
                            inputProps={{ step: 300 }}
                            disabled={!currentDay?.enabled}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper className={classes.sectionPaper} variant="outlined">
              <div className={classes.sectionHeader}>
                <div>
                  <Typography className={classes.sectionTitle}>
                    Conexões da empresa
                  </Typography>
                  <Typography className={classes.sectionDescription}>
                    Administre fila padrão, grupos e comportamento principal de cada
                    conexão.
                  </Typography>
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={handleOpenCreateConnection}
                >
                  Nova conexão
                </Button>
              </div>

              <Grid container spacing={2}>
                {whatsApps.map((whatsapp) => {
                  const draft =
                    connectionDrafts[whatsapp.id] || createConnectionDraft(whatsapp);

                  return (
                    <Grid item xs={12} lg={6} key={whatsapp.id}>
                      <div className={classes.connectionCard}>
                        <div className={classes.connectionTop}>
                          <div>
                            <Typography className={classes.connectionName}>
                              {whatsapp.name}
                            </Typography>
                            <Typography className={classes.connectionMeta}>
                              Status: {whatsapp.status || "OPENING"} •{" "}
                              {whatsapp.updatedAt
                                ? new Date(whatsapp.updatedAt).toLocaleString("pt-BR")
                                : "-"}
                            </Typography>
                          </div>
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<Edit />}
                            onClick={() => handleOpenEditConnection(whatsapp.id)}
                          >
                            Editar conexão
                          </Button>
                        </div>

                        <div className={classes.toggleRow}>
                          <FormControlLabel
                            control={
                              <Switch
                                color="primary"
                                checked={Boolean(draft.isDefault)}
                                onChange={(event) =>
                                  handleConnectionDraftChange(
                                    whatsapp.id,
                                    "isDefault",
                                    event.target.checked
                                  )
                                }
                              />
                            }
                            label="Conexão padrão"
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                color="primary"
                                checked={Boolean(draft.allowGroupMessages)}
                                onChange={(event) =>
                                  handleConnectionDraftChange(
                                    whatsapp.id,
                                    "allowGroupMessages",
                                    event.target.checked
                                  )
                                }
                              />
                            }
                            label="Atender grupos"
                          />
                        </div>

                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Fila padrão da conexão</InputLabel>
                          <Select
                            value={draft.defaultQueueId || ""}
                            onChange={(event) =>
                              handleConnectionDraftChange(
                                whatsapp.id,
                                "defaultQueueId",
                                event.target.value
                              )
                            }
                            label="Fila padrão da conexão"
                          >
                            <MenuItem value="">
                              <em>Sem fila padrão</em>
                            </MenuItem>
                            {(whatsapp.queues || []).map((queue) => (
                              <MenuItem key={queue.id} value={queue.id}>
                                {queue.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <Typography className={classes.queuesHint}>
                          Filas disponíveis nesta conexão:{" "}
                          {(whatsapp.queues || []).length > 0
                            ? whatsapp.queues.map((queue) => queue.name).join(", ")
                            : "nenhuma fila vinculada"}
                        </Typography>

                        <div className={classes.connectionActions}>
                          <Typography className={classes.connectionMeta}>
                            {draft.defaultQueueId
                              ? "Novos tickets dessa conexão serão direcionados para a fila padrão."
                              : "Sem fila padrão, o comportamento atual de escolha de fila é mantido."}
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleSaveConnection(whatsapp.id)}
                          >
                            Salvar conexão
                          </Button>
                        </div>
                      </div>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </div>
    </MainContainer>
  );
};

export default CompanyPage;
